import { withSafeRender } from "~/components/atoms/WvSafeRender";
import type { Breadcrumb, CustomField, ProductAttributeSearchFilter, WovarCategory, WovarCustomer } from "@commercekit/api";
import type { HTMLComponent } from "@commercekit/react";
import type { FaqSliceSlice } from "prismicio-types";
import type { CategoryPageResultType } from "~/routes-server/$slug/types";
import classNames from "classnames";
import WvBreadcrumbs from "../atoms/WvBreadcrumbs";
import WvSearchFilter from "../organisms/WvSearchFilter";
import WvCategoriesInHexagon from "../organisms/WvCategoriesInHexagon";
import { useMemo } from "react";
import { SliceZoneC } from "@commercekit/prismic";
import { fixRelativeLinks, wrapIframe } from "@commercekit/shared";
import { FaqSection } from "~/lib/prismic";
import { useFeatureToggle } from "~/hooks/useFeatureToggle";
import { CATEGORY_PAGINATION_TOGGLE } from "~/lib/featureToggle";
import { useLoadMoreProducts } from "~/hooks/useLoadMoreProducts";
import { useLocation, useNavigation } from "@remix-run/react";
import { useCustomerSimple } from "@commercekit/remix";

// Hoisted: rebuilding the map per render would remount the slice each time.
const faqComponents = withSafeRender({ faq_slice: FaqSection });

const WvBaseCategoryPage: HTMLComponent<{
  categoryPageResult: CategoryPageResultType;
}> = ({ categoryPageResult }) => {
  const {
    categoryData,
    categoryDocument,
    categoryBreadcrumbs,
    products,
    filters,
    availableFilters,
    highlightedFilter,
    pagination,
    uspBarImageAndTextSlices,
  } = categoryPageResult;

  const { customer } = useCustomerSimple();

  const { state, location } = useNavigation();
  const currentLocation = useLocation();
  const isProductListEnabled = useFeatureToggle("category-product-list", false);
  // WOV-2195: the A/B variant swaps infinite scroll for numbered page links.
  const isPaginationEnabled = useFeatureToggle(CATEGORY_PAGINATION_TOGGLE, false);
  const currentPage = useMemo(() => {
    const parsed = Number.parseInt(
      new URLSearchParams(currentLocation.search).get("page") || "",
      10
    );
    return Number.isFinite(parsed) && parsed > 1 ? parsed : 1;
  }, [currentLocation.search]);

  const faqSlice = categoryDocument?.data.slices.filter(
    (slice) =>
      slice.slice_type === "faq_slice" && slice.variation === "categoryFaq"
  ) as FaqSliceSlice[];

  const pageLoading = useMemo(
    () => state === "loading" && location.pathname === currentLocation.pathname,
    [currentLocation.pathname, location?.pathname, state]
  );

  const { exhibitProduct, exhibitFilter, loadMoreLoading, loadRef } = useLoadMoreProducts(
    products,
    categoryData?.id as string,
    availableFilters,
    pagination,
    !isPaginationEnabled
  );

  const descriptions = useMemo(
    () => ({
      long: fixRelativeLinks(wrapIframe(categoryData?.longDescription)),
      short: fixRelativeLinks(wrapIframe(categoryData?.shortDescription)),
    }),
    [categoryData?.longDescription, categoryData?.shortDescription]
  );

  const activeFilters = useMemo(
    () => (products.length > 0 ? exhibitFilter ?? [] : []),
    [products, exhibitFilter]
  );

  const filterCountMap = useMemo(() => {
    const map: Record<string, Record<string, number>> = {};
    for (const group of filters ?? []) {
      if (!group.name || !group.options) continue;
      map[group.name] = {};
      for (const opt of group.options) {
        map[group.name][opt.value] = opt.count;
      }
    }
    return map;
  }, [filters]);

  const mappedFilters: ProductAttributeSearchFilter[] = useMemo(
    () =>
      activeFilters
        .filter(
          (i: CustomField) =>
            i.displayName &&
            i.value?.stringValues &&
            i.value.stringValues.length
        )
        .map(({ name, displayName, value }: CustomField) => ({
          name: displayName,
          options: value.stringValues.map((o: string) => ({
            name: displayName,
            key: name,
            value: o,
            count: filterCountMap[name]?.[o] ?? 0,
          })),
        }))
        .slice(0, 20),
    [activeFilters, filterCountMap]
  );

  const normalizedHighlightedFilter = useMemo(() => {
    if (!highlightedFilter) {
      return { name: "", options: [] };
    }

    return {
      name: highlightedFilter.displayName,
      options: (highlightedFilter.value?.stringValues ?? []).map((o: string) => ({
        name: o,
        key: highlightedFilter.name,
        value: o,
        count: filterCountMap[highlightedFilter.name]?.[o] ?? 0,
      })),
    };
  }, [highlightedFilter, filterCountMap]);

  return (
    <section className="webshop-category-list">
      <div className="container">
        {categoryBreadcrumbs.length > 1 && (
          <WvBreadcrumbs breadcrumbs={categoryBreadcrumbs as Breadcrumb[]} />
        )}

        <WvCategoriesInHexagon categoryData={categoryData as WovarCategory} />

        {isProductListEnabled && exhibitProduct && exhibitProduct.length > 0 && (
          <div
            className={classNames(
              "productlist",
              pageLoading || loadMoreLoading ? "productlist--loading" : ""
            )}
          >
            <WvSearchFilter
              filters={mappedFilters}
              quickMobileFilters={normalizedHighlightedFilter}
              products={exhibitProduct}
              isProductLoading={pageLoading}
              totalProductCount={pagination?.total ?? 0}
              uspBarImageAndTextSlices={uspBarImageAndTextSlices}
              richTextSlices={[]}
              faqSlice={[]}
              categoryData={categoryData as WovarCategory}
              loadRef={loadRef}
              customer={customer as WovarCustomer}
              showTotalCount={true}
              showDescriptions={false}
              usePagination={isPaginationEnabled}
              currentPage={currentPage}
            />
          </div>
        )}

        {faqSlice && faqSlice.length > 0 ? (
          <SliceZoneC
            slices={faqSlice}
            components={faqComponents}
            defaultComponent={({ slice }) => <div>{slice.slice_type}</div>}
          />
        ) : null}

        <div className="row mb-5">
          <div className="col">
            <h2 className="heading strong mdl">{categoryData?.name}</h2>
            <div className="row content">
              <div
                className="col-12 col-lg-6"
                dangerouslySetInnerHTML={{
                  __html: descriptions.long || "",
                }}
              ></div>
              <div
                className="col-12 col-lg-6"
                dangerouslySetInnerHTML={{
                  __html: descriptions.short || "",
                }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
export default WvBaseCategoryPage;
